/**
 * `/notifications/deliveries` — the outbox.
 *
 * A message is queued inside the transaction that caused it, so nothing is
 * announced that was then rolled back, and the worker delivers afterwards. This
 * screen is the view of that queue: what is waiting, what failed, and what has
 * been retried past the point of trying again.
 *
 * Two buttons, and they are not the same thing:
 *
 * - **Retry** puts one dead or failed delivery back in the queue.
 * - **Drain** runs the worker once, now, against **real destinations**. It is an
 *   operational act, not a test, which is why it needs `notification.manage`
 *   rather than `notification.send_test` and why it is behind a confirmation.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import BoltIcon from '@mui/icons-material/Bolt';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import SendIcon from '@mui/icons-material/Send';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { DrainResultDto } from '@forsys/shared';
import { AsyncContent } from '../../components/AsyncContent';
import { EntityTable } from '../../components/EntityTable';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorState } from '../../components/ErrorState';
import { PageHeader } from '../../components/PageHeader';
import { Timestamp } from '../../components/Timestamp';
import { useAsync } from '../../hooks/useAsync';
import { useListQuery } from '../../hooks/useListQuery';
import { useSubmit } from '../../hooks/useSubmit';
import { useAuth } from '../../auth/useAuth';
import { drainQueue, listDeliveries, retryDelivery } from '../../api/notifications';

/** `dead` is the one that needs a person; the rest resolve themselves. */
function statusColour(status: string): 'default' | 'info' | 'success' | 'warning' | 'error' {
  switch (status) {
    case 'sent':
      return 'success';
    case 'pending':
      return 'info';
    case 'failed':
      return 'warning';
    case 'dead':
      return 'error';
    default:
      return 'default';
  }
}

/** The delivery statuses, as keys. The API value is the identifier, not the label. */
const STATUS_KEYS = {
  pending: 'status.pending',
  sent: 'status.sent',
  failed: 'status.failed',
  dead: 'status.dead',
} as const;

type StatusKey = (typeof STATUS_KEYS)[keyof typeof STATUS_KEYS];

function statusKeyFor(status: string): StatusKey | undefined {
  return (STATUS_KEYS as Record<string, StatusKey | undefined>)[status];
}

export default function DeliveriesPage() {
  const { t } = useTranslation(['notifications', 'common']);
  const { can } = useAuth();

  /** The label for a status, falling back to the raw value for one this build does not know. */
  function statusLabel(status: string): string {
    const key = statusKeyFor(status);
    return key ? t(key) : status;
  }
  const [status, setStatus] = useState('');
  const [confirmDrain, setConfirmDrain] = useState(false);
  const [drained, setDrained] = useState<DrainResultDto | null>(null);

  // Newest first by default, which is what "what just broke" means.
  const list = useListQuery({ defaultSort: 'id', defaultOrder: 'desc', resetKey: status });
  const deliveries = useAsync(
    () => listDeliveries({ ...list.query, ...(status ? { status } : {}) }),
    [status, list.key],
  );

  const mayManage = can('notification.manage');
  const mayRetry = can('notification.send_test');

  const retry = useSubmit(async (id: number) => {
    const result = await retryDelivery(id);
    deliveries.reload();
    return result;
  });

  const drain = useSubmit(async () => {
    const { result } = await drainQueue();
    setDrained(result);
    setConfirmDrain(false);
    deliveries.reload();
    return result;
  });

  return (
    <>
      <PageHeader
        title={t('notifications:deliveries.title')}
        description={t('notifications:deliveries.description')}
        actions={
          <>
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => deliveries.reload()}
              disabled={deliveries.loading}
            >
              {t('common:action.refresh')}
            </Button>
            {mayManage && (
              <Button
                variant="contained"
                startIcon={<BoltIcon />}
                onClick={() => setConfirmDrain(true)}
              >
                {t('notifications:deliveries.drain')}
              </Button>
            )}
          </>
        }
      />

      {drained && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDrained(null)}>
          {t('notifications:deliveries.drained', {
            claimed: drained.claimed,
            sent: drained.sent,
            failed: drained.failed,
            dead: drained.dead,
          })}
        </Alert>
      )}
      {retry.error && <ErrorState error={retry.error} />}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label={t('notifications:deliveries.status')}
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">{t('notifications:deliveries.allStatuses')}</MenuItem>
          <MenuItem value="pending">{t('notifications:status.pending')}</MenuItem>
          <MenuItem value="sent">{t('notifications:status.sent')}</MenuItem>
          <MenuItem value="failed">{t('notifications:status.failed')}</MenuItem>
          <MenuItem value="dead">{t('notifications:status.dead')}</MenuItem>
        </TextField>
      </Stack>

      <AsyncContent state={deliveries}>
        {({ deliveries: rows, page }) => (
          <EntityTable
            rows={rows}
            rowKey={(delivery) => delivery.id}
            list={list}
            page={page}
            actionsWidth={80}
            empty={{
              icon: <SendIcon fontSize="inherit" />,
              title: t('notifications:deliveries.emptyTitle'),
              description: status
                ? t('notifications:deliveries.emptyFiltered', { status: statusLabel(status) })
                : t('notifications:deliveries.emptyPlain'),
            }}
            columns={[
              {
                field: 'status',
                label: t('notifications:deliveries.column.status'),
                sortable: true,
                width: 90,
                render: (delivery) => (
                  <Chip
                    size="small"
                    color={statusColour(delivery.status)}
                    label={statusLabel(delivery.status)}
                  />
                ),
              },
              // Subject and channel live on the joined message row; the queue
              // query sorts on the delivery, which is what an operator is
              // actually triaging.
              {
                field: 'subject',
                label: t('notifications:deliveries.column.subject'),
                render: (delivery) => (
                  <Stack spacing={0.25}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <Typography variant="body2">
                        {delivery.subject ?? t('common:state.empty')}
                      </Typography>
                      {delivery.messageCount > 1 && (
                        <Tooltip title={t('notifications:deliveries.coalesced')}>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`×${delivery.messageCount}`}
                          />
                        </Tooltip>
                      )}
                      {delivery.severity && (
                        <Typography variant="caption" color="text.secondary">
                          {delivery.severity}
                        </Typography>
                      )}
                    </Stack>
                    {delivery.lastError && (
                      <Typography variant="caption" color="error.main">
                        {delivery.lastError}
                      </Typography>
                    )}
                  </Stack>
                ),
              },
              {
                field: 'channel',
                label: t('notifications:deliveries.column.channel'),
                width: 150,
                render: (delivery) => (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      {delivery.channelName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {delivery.transport}
                    </Typography>
                  </>
                ),
              },
              {
                field: 'attempts',
                label: t('notifications:deliveries.column.attempts'),
                sortable: true,
                width: 90,
                render: (delivery) => delivery.attempts,
              },
              {
                field: 'nextAttemptAt',
                label: t('notifications:deliveries.column.nextAttempt'),
                sortable: true,
                width: 150,
                render: (delivery) => (
                  <Typography variant="caption" color="text.secondary">
                    <Timestamp value={delivery.nextAttemptAt} />
                  </Typography>
                ),
              },
            ]}
            actions={(delivery) => [
              {
                key: 'retry',
                label: t('notifications:deliveries.retryTooltip'),
                icon: <ReplayIcon fontSize="small" />,
                onClick: () => void retry.submit(delivery.id),
                disabled: retry.pending,
                // Only for the two that have stopped moving on their own. A
                // pending one is already going to be tried, and a button saying
                // otherwise invites double-sending.
                hidden:
                  !mayRetry || (delivery.status !== 'failed' && delivery.status !== 'dead'),
              },
            ]}
          />
        )}
      </AsyncContent>

      <ConfirmDialog
        open={confirmDrain}
        title={t('notifications:deliveries.drainDialog.title')}
        confirmLabel={t('notifications:deliveries.drainDialog.confirm')}
        pending={drain.pending}
        error={drain.error}
        onClose={() => setConfirmDrain(false)}
        onConfirm={() => void drain.submit()}
        message={t('notifications:deliveries.drainDialog.message')}
      />
    </>
  );
}
